import { t } from "@travily/i18n";
import communityIllustration from "@ui/assets/illustrations/community-animated.webp";
import { TileCheckboxGroup } from "@ui/components/TileCheckboxGroup";
import { TileRadioGroup } from "@ui/components/TileRadioGroup";
import { Wizard } from "@ui/components/Wizard";
import { useBreakpoint } from "@ui/hooks/use-breakpoint";
import type { ReactNode } from "react";

const COLOR = "yellow" as const;

export interface CommunityFormatStepViewProps {
  currentStage: number;
  totalStages: number;
  hubCount: number;
  selectedValues: string[];
  primaryPending: boolean;
  error?: string;
  topBarActions: ReactNode;
  onBack?: () => void;
  onPrimary: () => void;
  onSelectionChange: (values: string[]) => void;
}

export const CommunityFormatStepView = ({
  currentStage,
  totalStages,
  hubCount,
  selectedValues,
  primaryPending,
  error,
  topBarActions,
  onBack,
  onPrimary,
  onSelectionChange,
}: CommunityFormatStepViewProps) => {
  const isAboveMd = useBreakpoint("md");
  const isAboveLg = useBreakpoint("lg");
  const selectionGroupSize = isAboveLg ? "5xl" : isAboveMd ? "4xl" : "3xl";

  const isMultipleSelection = hubCount > 1;

  const formatOptions = [
    {
      value: "year_round",
      label: t("web.founding_members.creator.steps.community.format.show.options.year_round", { count: hubCount }),
    },
    {
      value: "seasonal",
      label: t("web.founding_members.creator.steps.community.format.show.options.seasonal", { count: hubCount }),
    },
    {
      value: "popup",
      label: t("web.founding_members.creator.steps.community.format.show.options.popup", { count: hubCount }),
    },
    {
      value: "travelling",
      label: t("web.founding_members.creator.steps.community.format.show.options.travelling", { count: hubCount }),
    },
  ];

  return (
    <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
      {topBarActions}
      <Wizard.PrimaryButton onPress={onPrimary} isPending={primaryPending}>
        {t("common.actions.continue")}
      </Wizard.PrimaryButton>
      <Wizard.Stage
        illustration={<img alt="" aria-hidden="true" src={communityIllustration} />}
        heading={t("web.founding_members.creator.steps.community.format.show.title", { count: hubCount })}
        helper={
          isMultipleSelection ? t("web.founding_members.creator.steps.community.format.show.subtitle") : undefined
        }
      >
        {isMultipleSelection ? (
          <TileCheckboxGroup
            className="w-full max-w-[600px] gap-5"
            color={COLOR}
            onChange={onSelectionChange}
            size={selectionGroupSize}
            value={selectedValues}
          >
            {formatOptions.map((option) => (
              <TileCheckboxGroup.Item key={option.value} value={option.value}>
                {option.label}
              </TileCheckboxGroup.Item>
            ))}
          </TileCheckboxGroup>
        ) : (
          <TileRadioGroup
            className="w-full max-w-[600px] gap-5"
            color={COLOR}
            onChange={(value) => onSelectionChange([value])}
            size={selectionGroupSize}
            value={selectedValues[0]}
          >
            {formatOptions.map((option) => (
              <TileRadioGroup.Item key={option.value} value={option.value}>
                {option.label}
              </TileRadioGroup.Item>
            ))}
          </TileRadioGroup>
        )}
        {error ? <Wizard.FieldError>{error}</Wizard.FieldError> : null}
      </Wizard.Stage>
    </Wizard>
  );
};
